import { Modal } from "@/shared/ui";
import { useAppSelector, useAppDispatch } from "@/shared/hooks/store";
import { createContext, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { FirstMedicationPopup } from "./components/medications/first-medication-popup";
import { ABSOLUTE_ROUTES } from "@/shared/constants/routes";
import EligibilityFailedModal from "@/shared/components/eligibility-failed-modal";
import { selectOnboardingUser, selectSelectedMedication } from "@/store/slices/onboarding/selectors";
import { ITransferPrescriptions } from "@/shared/types/interfaces/transfer-prescriptions";
import { runEligibilityGateClaim } from '@/shared/helpers/test-claims-combo';
import { setEligibilityFailedModalOpen } from "../../../../store/slices/onboarding";
import { CustomersController } from '@/shared/endpoints/customers';


interface MedicationContextType {
    openFirstMedicationPopup: () => void;
    handleAddMedication: (medication: ITransferPrescriptions | null) => void;
}

export const MedicationContext = createContext<MedicationContextType | null>(null);

const MyMedboxOutletWithContext = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const user = useAppSelector(selectOnboardingUser)!;
    const selectedMedication = useAppSelector(selectSelectedMedication);
    const [showFirstMedicationPopup, setShowFirstMedicationPopup] = useState(false);

    const openFirstMedicationPopup = () => {
        setShowFirstMedicationPopup(true);
    }

    const closeFirstMedicationPopup = () => {
        setShowFirstMedicationPopup(false);
    }

    const handleAddMedication = async (prescription: ITransferPrescriptions | null) => {
        if (!user.userOnboarding?.details) return;
        const firstMedicationPopupShowed = !!user.userOnboarding?.details?.firstMedicationPopupShowed;
        let additionalMetaData = {}
        if (selectedMedication?.quantity && selectedMedication?.daysSupply) {
            const quantitesMap = { ...(user.userOnboarding?.details.prescriptionCopayQuantities || {}) };
            quantitesMap[selectedMedication.prescribable.medid] = {
                quantity: selectedMedication.quantity,
                daysSupply: selectedMedication.daysSupply
            };

            additionalMetaData = { ...additionalMetaData, prescriptionCopayQuantities: quantitesMap };
        }
        if (!firstMedicationPopupShowed) {
            additionalMetaData = { ...additionalMetaData, firstMedicationPopupShowed: true };
        }

        await dispatch(CustomersController.updateMetadata({
            ...user.userOnboarding!,
            details: {
                ...user.userOnboarding!.details,
                ...additionalMetaData
            } as unknown as typeof user.userOnboarding.details
        }));

        if (prescription) {
            const updatedPrescriptions = [...user.transferPrescriptions, prescription];
            const eligible = await runEligibilityGateClaim();
            const { prescriptionCount, otcCount } = updatedPrescriptions.reduce(
                (acc, p) => {
                    if (p.otc) acc.otcCount++;
                    else acc.prescriptionCount++;
                    return acc;
                },
                { prescriptionCount: 0, otcCount: 0 }
            );

            if (prescriptionCount === 1 && eligible === false) {
                dispatch(setEligibilityFailedModalOpen(true));
            } else if (prescriptionCount + otcCount === 1 && !firstMedicationPopupShowed) {
                openFirstMedicationPopup();
            }
        }

        navigate(ABSOLUTE_ROUTES.ONBOARDING.PRESCRIPTIONS.MEDICATIONS, { state: { fromAddMedication: true } });
    }

    return (
        <MedicationContext.Provider value={{ openFirstMedicationPopup, handleAddMedication }}>
            <div>
                <Outlet />
                <Modal
                    open={showFirstMedicationPopup}
                    onClose={closeFirstMedicationPopup}
                    dataTestId="first-medication"
                >
                    <FirstMedicationPopup handleAddMore={() => {
                        closeFirstMedicationPopup();
                        navigate(ABSOLUTE_ROUTES.ONBOARDING.PRESCRIPTIONS.ADD_MEDICATION_FLOW.ADD_MEDICATION);
                    }} />
                </Modal>

                <EligibilityFailedModal />
            </div>
        </MedicationContext.Provider>
    )
}

export default MyMedboxOutletWithContext;